/**
 * Subscription Management Type Definitions
 * Types for subscription plans, modules, and organization subscriptions
 */

export enum BillingInterval {
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
}

export enum Currency {
  DZD = 'DZD',
  EUR = 'EUR',
  USD = 'USD',
}

export enum ModuleCategory {
  CORE = 'core',
  OPERATIONS = 'operations',
  SALES = 'sales',
  LOGISTICS = 'logistics',
  ANALYTICS = 'analytics',
  INTEGRATIONS = 'integrations',
}

export enum SubscriptionStatus {
  ACTIVE = 'active',
  PAST_DUE = 'past_due',
  CANCELED = 'canceled',
  TRIALING = 'trialing',
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  slug: string;
  description: string;
  tier: SubscriptionTier;
  basePrice: number;
  currency: Currency;
  billingInterval: BillingInterval;
  maxUsers: number | null;
  maxStores: number | null;
  maxWarehouses: number | null;
  isActive: boolean;
  displayOrder: number;
  metadata: Record<string, any>;
  createdAt: string;
  updatedAt: string;
  modules?: PlanModule[];
}

export type SubscriptionTier = 'free' | 'starter' | 'pro' | 'professional' | 'enterprise';

export interface Module {
  id: string;
  key: string;
  name: string;
  description: string;
  category: ModuleCategory;
  isCore: boolean;
  icon: string;
  dependencies: string[];
  settingsSchema: Record<string, any>;
  // Tier-based access control
  tierMinimum: SubscriptionTier | null; // Minimum tier required (null = plan-based only)
  isAddOn: boolean; // Can be purchased separately for any tier
  createdAt: string;
  updatedAt: string;
}

export interface PlanModule {
  id: string;
  planId: string;
  moduleId: string;
  isIncluded: boolean;
  addOnPrice: number | null;
  createdAt: string;
  plan?: SubscriptionPlan;
  module?: Module;
}

export interface OrganizationSubscription {
  id: string;
  organizationId: string;
  planId: string;
  status: SubscriptionStatus;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  trialEndsAt: string | null;
  canceledAt: string | null;
  cancellationReason: string | null;
  metadata: Record<string, any>;
  createdAt: string;
  updatedAt: string;
  plan?: SubscriptionPlan;
  organization?: any;
}

export interface OrganizationModule {
  id: string;
  organizationId: string;
  moduleId: string;
  enabledAt: string;
  disabledAt: string | null;
  isTrial: boolean;
  trialEndsAt: string | null;
  settings: Record<string, any>;
  createdAt: string;
  updatedAt: string;
  module?: Module;
  organization?: any;
}

// DTOs for API requests
export interface CreatePlanDto {
  name: string;
  slug: string;
  description: string;
  basePrice: number;
  currency?: Currency;
  billingInterval: BillingInterval;
  maxUsers?: number | null;
  maxStores?: number | null;
  maxWarehouses?: number | null;
  isActive?: boolean;
  displayOrder?: number;
  metadata?: Record<string, any>;
}

export interface UpdatePlanDto extends Partial<CreatePlanDto> {}

export interface CreateModuleDto {
  key: string;
  name: string;
  description: string;
  category: ModuleCategory;
  isCore?: boolean;
  icon: string;
  dependencies?: string[];
  settingsSchema?: Record<string, any>;
}

export interface UpdateModuleDto extends Partial<CreateModuleDto> {}

export interface AssignSubscriptionDto {
  planId: string;
  organizationId: string;
  isTrial?: boolean;
  trialDays?: number;
  metadata?: Record<string, any>;
}

export type LimitsHandling = 'keep' | 'apply' | 'custom';

export interface CustomLimitsDto {
  includedStores?: number;
  includedWarehouses?: number;
  includedStaff?: number;
  maxProducts?: number;
  maxClients?: number;
  maxLocations?: number;
  salesRateLimit?: number;
  offlineTransactionLimit?: number;
}

export interface UpdateSubscriptionDto {
  planId?: string;
  status?: SubscriptionStatus;
  metadata?: Record<string, any>;
  limitsHandling?: LimitsHandling;
  customLimits?: CustomLimitsDto;
}

export interface CancelSubscriptionDto {
  cancellationReason?: string;
}

export interface AssignModuleToPlanDto {
  moduleId: string;
  isIncluded?: boolean;
  addOnPrice?: number;
}

export interface UpdatePlanModuleDto {
  isIncluded?: boolean;
  addOnPrice?: number;
}

// Module Dependency Management Types
export interface ModuleInfoDto {
  id: string;
  key: string;
  name: string;
  description: string;
  category: ModuleCategory;
  isCore: boolean;
  isEnabled?: boolean;
}

export interface ValidateEnableModuleResponse {
  valid: boolean;
  missingDependencies: ModuleInfoDto[];
  willAutoEnable: ModuleInfoDto[];
}

export interface ValidateDisableModuleResponse {
  valid: boolean;
  affectedModules: ModuleInfoDto[];
  willCascadeDisable: ModuleInfoDto[];
}

export interface EnableModuleResponse {
  message: string;
  module: ModuleInfoDto;
  autoEnabledModules: ModuleInfoDto[];
}

export interface DisableModuleResponse {
  message: string;
  module: ModuleInfoDto;
  cascadeDisabledModules: ModuleInfoDto[];
}

export interface DependencyGraphNode {
  id: string;
  key: string;
  name: string;
  category: ModuleCategory;
  isEnabled?: boolean;
}

export interface DependencyGraphEdge {
  from: string;
  to: string;
}

export interface DependencyGraphResponse {
  nodes: DependencyGraphNode[];
  edges: DependencyGraphEdge[];
}

export interface EnableModuleDto {
  isTrial?: boolean;
  trialDays?: number;
  autoResolveDependencies?: boolean;
}

export interface DisableModuleDto {
  force?: boolean;
}

export interface UpdateModuleSettingsDto {
  settings: Record<string, any>;
}

// Analytics types
export interface SubscriptionMetrics {
  totalOrganizations: number;
  activeSubscriptions: number;
  trialingSubscriptions: number;
  canceledSubscriptions: number;
  monthlyRecurringRevenue: number;
  annualRecurringRevenue: number;
  averageRevenuePerOrg: number;
  churnRate: number;
  trialConversionRate: number;
  organizationsByPlan: { planName: string; count: number }[];
  moduleAdoption: { moduleName: string; count: number; percentage: number }[];
  revenueByMonth: { month: string; revenue: number }[];
}

// UI Helper types
export interface PlanWithModules extends SubscriptionPlan {
  includedModules: Module[];
  addOnModules: { module: Module; price: number }[];
}

export interface OrganizationWithSubscription {
  id: string;
  name: string;
  subscription: OrganizationSubscription | null;
  activeModules: OrganizationModule[];
}
